import { useApi } from '~/composables/useApi'
import { useSupabaseClient } from '~/composables/useSupabaseClient'
import type { CheckFile } from '~/entities/checkFile'
import type { DailyWorkFile, DailyWorkFileCreateParams } from '~/entities/dailyWorkFile'
import type { Operator } from '~/entities/operator'
import type { WorkItem } from '~/entities/workItem'
import type { WorkSheet } from '~/entities/workSheet'
import { type Dayjs, formatDate } from '~/utils/date'

export const getDailyWorkFile = async (checkFileId: CheckFile['id'], targetDate: Dayjs) => {
  const { data, error } = await useSupabaseClient()
    .from('daily_work_files')
    .select('*, work_sheets(*, work_items(*))')
    .eq('check_file_id', checkFileId)
    .eq('work_date', formatDate(targetDate))
    .maybeSingle()
  if (error) throw error
  return data
}
export type GetDailyWorkFileReturn = NonNullable<Awaited<ReturnType<typeof getDailyWorkFile>>>

export const getDailyWorkFileById = async (dailyWorkFileId: DailyWorkFile['id']) => {
  const { data, error } = await useSupabaseClient()
    .from('daily_work_files')
    .select('*, work_sheets(*, work_items(*))')
    .eq('id', dailyWorkFileId)
    .maybeSingle()
  if (error) throw error
  return data
}

export const getDailyWorkFilesByCheckFileIds = async (
  checkFileIds: CheckFile['id'][],
  targetDate: Dayjs
) => {
  const { data, error } = await useSupabaseClient()
    .from('daily_work_files')
    .select('*')
    .in('check_file_id', checkFileIds)
    .eq('work_date', formatDate(targetDate))
  if (error) throw error
  return data
}

export const getWipWorkFilesByCheckFileIds = async (checkFileIds: CheckFile['id'][]) => {
  const { data, error } = await useSupabaseClient()
    .from('daily_work_files')
    .select('*')
    .in('check_file_id', checkFileIds)
    .eq('status', 'WIP')
  if (error) throw error
  return data
}

export const getDailyWorkFileWithAllRelations = async (
  checkFileId: CheckFile['id'],
  targetDate: Dayjs,
  timeboxId?: WorkItem['timebox_id'],
  workSheetId?: WorkSheet['id']
) => {
  let query = useSupabaseClient()
    .from('daily_work_files')
    .select(
      '*, work_daily_verifiers(*), work_sheets!inner(*, work_items!inner(*, notes(*), tickets(*), work_number_conditions(*), correction:work_item_corrections(*)))'
    )
    .eq('check_file_id', checkFileId)
    .eq('work_date', formatDate(targetDate))
    .order('check_sheet_assignment_position', { referencedTable: 'work_sheets', ascending: true })
  if (timeboxId) query = query.eq('work_sheets.work_items.timebox_id', timeboxId)
  if (timeboxId === null) query = query.is('work_sheets.work_items.timebox_id', null)
  if (workSheetId) query = query.eq('work_sheets.id', workSheetId)

  const { data, error } = await query.maybeSingle()
  if (error) throw error

  return data
}

export const createDailyWorkFile = async (params: DailyWorkFileCreateParams) => {
  const { data, error } = await useSupabaseClient()
    .from('daily_work_files')
    .insert(params)
    .select()
    .single()
  if (error) throw error
  return data
}

type DeleteDailyWorkFileArgs = {
  workFileId: DailyWorkFile['id']
  locationId: DailyWorkFile['location_id']
}
// この関数は直接使わずrevertDailyWorkFileService経由で削除実行すること
export const deleteDailyWorkFile = async ({ workFileId, locationId }: DeleteDailyWorkFileArgs) => {
  const { client } = useApi()
  const response = await client.locations[':locationId'].dailyWorkFiles[':dailyWorkFileId'].$delete(
    {
      param: { locationId: String(locationId), dailyWorkFileId: String(workFileId) },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to delete daily work file: ${response.status} ${response.statusText}`)
  }
}

type SubmitDailyWorkFileArgs = {
  workFileId: DailyWorkFile['id']
  locationId: DailyWorkFile['location_id']
  submittedOperator: Operator
}
// この関数は直接使わずsubmitDailyWorkFileService経由で提出実行すること
export const submitDailyWorkFile = async ({
  workFileId,
  locationId,
  submittedOperator,
}: SubmitDailyWorkFileArgs) => {
  const { client } = useApi()
  // 提出者の氏名はサーバが operators から引くため、ID だけを送る
  const response = await client.locations[':locationId'].dailyWorkFiles[
    ':dailyWorkFileId'
  ].submit.$post({
    param: { locationId: String(locationId), dailyWorkFileId: String(workFileId) },
    json: { operatorId: submittedOperator.id },
  })

  if (!response.ok) {
    throw new Error(`Failed to submit daily work file: ${response.status} ${response.statusText}`)
  }
}

export const getDailyWorkFilesByRange = async (
  checkFileId: DailyWorkFile['check_file_id'],
  startDate: Dayjs,
  endDate: Dayjs,
  status?: DailyWorkFile['status'],
  limit?: number
) => {
  const query = useSupabaseClient()
    .from('daily_work_files')
    .select(
      '*, work_sheets!inner(*, work_items!inner(*, notes(*), tickets(*), work_number_conditions(*)))'
    )
    .eq('check_file_id', checkFileId)
    .gte('work_date', formatDate(startDate))
    .lte('work_date', formatDate(endDate))
    .order('work_date', { ascending: true })

  if (status) query.eq('status', status)
  if (limit) query.limit(limit)

  const { data, error } = await query
  if (error) throw error

  return data
}

// NOTE: useTodo のパフォーマンス改善のため、専用にクエリを発行するメソッド作成している
export const getWorkDateByRange = async (
  checkFileId: DailyWorkFile['check_file_id'],
  startDate: Dayjs,
  endDate: Dayjs,
  status: DailyWorkFile['status'],
  limit: number
) => {
  const query = useSupabaseClient()
    .from('daily_work_files')
    .select('work_date')
    .eq('check_file_id', checkFileId)
    .eq('check_file_type', 'SCHEDULED')
    .gte('work_date', formatDate(startDate))
    .lte('work_date', formatDate(endDate))
    .eq('status', status)
    .order('work_date', { ascending: true })
    .limit(limit)

  const { data, error } = await query
  if (error) throw error

  return data
}

export const getSubmittedDailyWorkFilesByScheduleDates = async (
  checkFileId: DailyWorkFile['check_file_id'],
  dates: string[]
) => {
  const query = useSupabaseClient()
    .from('daily_work_files')
    .select('*')
    .eq('check_file_id', checkFileId)
    .in('work_date', dates)
    .eq('status', 'SUBMITTED')

  const { data, error } = await query
  if (error) throw error
  return data
}

type NotifyDailyWorkFileSubmittedArgs = {
  workFileId: DailyWorkFile['id']
  locationId: DailyWorkFile['location_id']
}
// 提出を日次確認者にメールで通知する。宛先・本文・リンクはサーバが DB から組み立てるため ID だけを送る
export const notifyDailyWorkFileSubmitted = async ({
  workFileId,
  locationId,
}: NotifyDailyWorkFileSubmittedArgs) => {
  const { client } = useApi()
  const response = await client.locations[':locationId'].dailyWorkFiles[
    ':dailyWorkFileId'
  ].submittedNotifications.$post({
    param: { locationId: String(locationId), dailyWorkFileId: String(workFileId) },
  })

  if (!response.ok) {
    throw new Error(
      `Failed to notify daily work file submitted: ${response.status} ${response.statusText}`
    )
  }
}
